/* eslint-disable no-param-reassign */
/* eslint-disable default-case */
import debug from 'util/debug'
import { invert } from 'util/objects'

const order = [
  'channel:',
  'folder:',
  'team:',
  'agent:',
  'is:assigned',
  'is:unassigned',
  'draft:',
  'is:starred',
  'starred:',
  'is:unread',
  'is:open',
  'is:closed',
  'is:spam',
  'is:snoozed',
  'tag:',
  'deleted:',
]

// lookup table, for performance
const orderLookup = {}
order.forEach((key, index) => {
  orderLookup[key] = index
})

// normalize the query part to use the orderLookup table
function normalizePart(part) {
  const first3 = part.substr(0, 3)
  const startsWithIs = first3 === 'is:'
  if (startsWithIs) return part

  const firstPart = part.split(':')[0]
  return `${firstPart}:`
}

// this function normalizes the query string coming from realtime
// it DOES NOT support quoted strings, only ids
export function normalizeSearchQueryId(queryId) {
  // when there is nothing to re-sort, short circuit
  if (!queryId.match(/\s/)) return queryId

  const parts = queryId.split(' ')
  const sorted = parts.sort((a, b) => {
    const orderA = orderLookup[normalizePart(a)]
    const orderB = orderLookup[normalizePart(b)]
    return orderA > orderB ? 1 : -1
  })

  return sorted.join(' ')
}

function mapToField(fieldName) {
  return (_, value, filter) => {
    filter[fieldName] = value
    return filter
  }
}

function mapTeamToAssigned(_, value, filter) {
  if (value === 'none') {
    filter.assigned = {
      noTeam: true,
    }
  } else {
    filter.assigned = {
      team: value,
    }
  }
  return filter
}

function mapAgentToAssigned(_, value, filter) {
  if (value === 'none') {
    filter.assigned = {
      noAgent: true,
    }
  } else {
    filter.assigned = {
      agent: value,
    }
  }
  return filter
}

function mapIsToField(_, value, filter) {
  switch (value) {
    case 'assigned':
      debug('Mapping is:assigned is not supported')
      break
    case 'unassigned':
      filter.assigned = {
        noTeam: true,
        noAgent: true,
      }
      break
    case 'starred':
      filter.starred = true
      break
    case 'unread':
      filter.state = 'UNREAD'
      break
    case 'open':
      filter.state = 'OPEN'
      break
    case 'closed':
      filter.state = 'CLOSED'
      break
    case 'spam':
      filter.state = 'SPAM'
      break
    case 'snoozed':
      filter.state = 'SNOOZED'
      break
  }
  return filter
}

function mapDraftToDraftAuthor(_, value, filter) {
  filter.draftAuthor = value
  // TODO: Implement logic to convert username values to an author id
  return filter
}

function mapFieldToBoolean(_, value, filter) {
  // TODO: Support values like 0, 1, true, false, yes, no
  filter.starred = !!value
  return filter
}

function hackRoomFilter(filter) {
  // Currently room filters only support state and channel_id
  // They also dont "correctly" mimic the ConversationFilter, so
  // we need to do some hack.
  const roomFilter = {}
  if (filter.channelId) roomFilter.channelId = filter.channelId

  // The state on the roomFilter expects a RoomStateTypeEnum value
  // This should be one of OPEN, SNOOZED, CLOSED, SPAM, TRASH, MINE, UNASSIGNED
  if (filter.state) roomFilter.state = filter.state.toUpperCase()

  return roomFilter
}

function applyTypeFilterTransformations(filter) {
  const type = filter.type || 'widget'
  // The concept of a "RoomFilter vs ConversationFilter shouldnt exists"
  // Once we normalize the api, we need to get rid of these hacks
  delete filter.type
  if (type === 'widget') return hackRoomFilter(filter)
  return filter
}

function hackMapFolderState(key, value, filter, state) {
  switch (value) {
    case 'fol_0':
      filter.state = 'UNASSIGNED'
      break
    case 'fol_1':
      filter.state = 'MINE'
      break
    case 'fol_2':
      filter.state = 'OPEN'
      break
    case 'fol_3':
      filter.state = 'SNOOZED'
      break
    case 'fol_4':
      filter.state = 'CLOSED'
      break
    case 'fol_5':
      filter.state = 'TRASH'
      break
    case 'fol_6':
      filter.state = 'SPAM'
      break
    default:
      return mapToField('folderId')(key, value, filter, state)
  }
  return filter
}

const queryKeyToFilterMap = {
  channel: mapToField('channelId'),
  folder: hackMapFolderState,
  team: mapTeamToAssigned,
  agent: mapAgentToAssigned,
  is: mapIsToField,
  draft: mapDraftToDraftAuthor,
  starred: mapFieldToBoolean,
  tag: mapToField('tagName'),
  type: mapToField('type'),
  state: mapToField('state'),
}

export function constructGraphQLFilterObject(queryId, state) {
  if (!queryId) return null
  const queryParts = queryId.split(' ')
  return applyTypeFilterTransformations(
    queryParts.reduce((filter, queryPart) => {
      const [key, value] = queryPart.split(':')
      if (!queryKeyToFilterMap[key]) {
        debug(
          `CRITICAL: Unable to map query part to graphql filter input: ${queryPart}`
        )
        return filter
      }
      queryKeyToFilterMap[key](key, value, filter, state)
      return filter
    }, {})
  )
}

const queryStringMap = {
  channelId: 'channel',
  folderId: 'folder',
  teamId: 'team',
  agentId: 'agent',
  draftAuthorId: 'draft',
  tagId: 'tag',
  stateId: 'state',
  type: 'type',
  starred: 'starred',
}

export function queryStringToQueryId(query = {}) {
  const queryParts = []
  Object.keys(queryStringMap).forEach(key => {
    if (query[key] === undefined) return
    queryParts.push(`${queryStringMap[key]}:${query[key]}`)
  })
  if (query.is !== undefined) {
    query.is.split(`_`).forEach(isPart => {
      queryParts.push(`is:${isPart}`)
    })
  }
  return normalizeSearchQueryId(queryParts.join(' '))
}

const queryIdMap = invert(queryStringMap)

export function queryIdToQuery(queryId) {
  if (!queryId) return null

  const query = {}
  const queryParts = queryId.split(' ')
  queryParts.forEach(queryPart => {
    const [key, value] = queryPart.split(':')
    if (key === 'is') {
      if (!query.is) query.is = []
      query.is.push(value)
    } else if (queryIdMap[key] !== undefined) {
      query[queryIdMap[key]] = value
    }
  })
  if (query.is) {
    query.is = query.is.join('_')
  }
  return query
}
